// frontend/src/components/admin/CompanyForm.jsx
// ============================================================================
// FORMULAIRE CRÉATION/ÉDITION ENTREPRISE — THEME-AWARE (Light/Dark)
// Utilise le ThemeContext partagé depuis contexts/ThemeContext.js
// ============================================================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Building2, User, Calendar, AlertCircle, Plus, Trash2,
  Shield, Crown, Users, Database, CheckCircle, Info, Clock
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { Toast } from '../ui/Toast';
import { useTheme } from '../../contexts/ThemeContext';

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

/** Section card wrapper */
const Section = ({ icon: Icon, title, children, t }) => (
  <div className="rounded-xl border p-5 mb-5" style={{ backgroundColor: t.bgCard, borderColor: t.border }}>
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.primarySoft }}>
        <Icon size={16} style={{ color: t.primary }} />
      </div>
      <h3 className="text-[12px] font-bold uppercase tracking-[0.12em]" style={{ color: t.text }}>
        {title}
      </h3>
    </div>
    {children}
  </div>
);

/** Label */
const Label = ({ children, t }) => (
  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: t.textSecondary }}>
    {children}
  </label>
);

/** Input field themed */
const Input = ({ error, t, ...props }) => (
  <input
    {...props}
    className="w-full px-3.5 py-2.5 rounded-lg text-sm font-medium border outline-none transition-all duration-200"
    style={{
      backgroundColor: t.bgInput,
      borderColor: error ? t.dangerText + '60' : t.border,
      color: t.text,
    }}
    onFocus={(e) => {
      e.target.style.backgroundColor = t.bgInputFocus;
      e.target.style.borderColor = error ? t.dangerText : t.borderActive;
      e.target.style.boxShadow = `0 0 0 3px ${error ? t.dangerSoft : t.primarySoft}`;
    }}
    onBlur={(e) => {
      e.target.style.backgroundColor = t.bgInput;
      e.target.style.borderColor = error ? t.dangerText + '60' : t.border;
      e.target.style.boxShadow = 'none';
    }}
  />
);

/** Select field themed */
const Select = ({ error, t, children, ...props }) => (
  <select
    {...props}
    className="w-full px-3.5 py-2.5 rounded-lg text-sm font-medium border outline-none appearance-none cursor-pointer transition-all duration-200"
    style={{
      backgroundColor: t.bgInput,
      borderColor: error ? t.dangerText + '60' : t.border,
      color: t.text,
    }}
    onFocus={(e) => {
      e.target.style.borderColor = t.borderActive;
      e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`;
    }}
    onBlur={(e) => {
      e.target.style.borderColor = t.border;
      e.target.style.boxShadow = 'none';
    }}
  >
    {children}
  </select>
);

/** Error text */
const FieldError = ({ message, t }) => {
  if (!message) return null;
  return (
    <p className="mt-1 text-[11px] font-semibold flex items-center gap-1" style={{ color: t.dangerText }}>
      <AlertCircle size={11} />
      {message}
    </p>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const CompanyForm = ({ company = null, onClose, onSuccess }) => {
  const t = useTheme();
  const isEditing = !!company;

  // ── State ──
  const [formData, setFormData] = useState({
    company_name: '', company_slug: '', status: 'trial',
    trial_days: 30, max_users: 10, max_employees: 50,
    type_licence: 'basique', duree_licence_mois: 3,
  });
  const [users, setUsers] = useState([
    { name: '', email: '', password: '', role: 'admin' }
  ]);
  const [plans, setPlans] = useState({});
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // ── Toast ──
  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ── Plans ──
  useEffect(() => { loadPlans(); }, []);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await superAdminApi.licences.getPlans();
      if (response.success) setPlans(response.plans);
    } catch {
      setPlans({
        basique:   { max_employes: 25,  max_utilisateurs: 2, max_storage_mb: 1000 },
        standard:  { max_employes: 50,  max_utilisateurs: 4, max_storage_mb: 5000 },
        premium:   { max_employes: 150, max_utilisateurs: 6, max_storage_mb: 20000 },
      });
    } finally {
      setLoadingPlans(false);
    }
  };

  // ── Ajuster users selon licence ──
  useEffect(() => {
    if (!loadingPlans && plans[formData.type_licence]) {
      const maxUsers = plans[formData.type_licence].max_utilisateurs || 2;
      if (users.length !== maxUsers) {
        const newUsers = [];
        for (let i = 0; i < maxUsers; i++) {
          newUsers.push(users[i] || { name: '', email: '', password: '', role: i === 0 ? 'admin' : 'user' });
        }
        setUsers(newUsers);
      }
    }
  }, [formData.type_licence, loadingPlans, plans]);

  // ── Init mode édition ──
  useEffect(() => {
    if (company) {
      setFormData({
        company_name: company.company_name || '', company_slug: company.company_slug || '',
        status: company.status || 'trial', trial_days: 30,
        max_users: company.max_users || 10, max_employees: company.max_employees || 50,
        type_licence: 'basique', duree_licence_mois: 3,
      });
    }
  }, [company]);

  // ── Handlers ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'company_name' && !isEditing) {
      const slug = value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, company_slug: slug }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleUserChange = (index, field, value) => {
    const newUsers = [...users];
    newUsers[index] = { ...newUsers[index], [field]: value };
    setUsers(newUsers);
    if (errors[`user_${index}_${field}`]) {
      setErrors(prev => { const n = { ...prev }; delete n[`user_${index}_${field}`]; return n; });
    }
  };

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!formData.company_name.trim()) e.company_name = 'Le nom est requis';
    if (!formData.company_slug.trim()) e.company_slug = 'Le slug est requis';

    if (!isEditing) {
      const admin = users[0];
      if (!admin.name.trim()) e['user_0_name'] = 'Requis';
      if (!admin.email.trim()) e['user_0_email'] = 'Requis';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.email)) e['user_0_email'] = 'Invalide';
      if (!admin.password || admin.password.length < 6) e['user_0_password'] = 'Min. 6 car.';

      users.slice(1).forEach((user, idx) => {
        const i = idx + 1;
        const hasData = user.name.trim() || user.email.trim() || user.password;
        if (hasData) {
          if (!user.name.trim()) e[`user_${i}_name`] = 'Requis';
          if (!user.email.trim()) e[`user_${i}_email`] = 'Requis';
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) e[`user_${i}_email`] = 'Invalide';
          if (!user.password || user.password.length < 6) e[`user_${i}_password`] = 'Min. 6 car.';
        }
      });

      const filled = users.filter(u => u.email.trim());
      const emails = filled.map(u => u.email.toLowerCase());
      const dupes = emails.filter((em, i) => emails.indexOf(em) !== i);
      dupes.forEach(em => {
        const i = users.findIndex(u => u.email.toLowerCase() === em);
        e[`user_${i}_email`] = 'Déjà utilisé';
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ──
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (isEditing) {
        await superAdminApi.companies.update(company.id, {
          company_name: formData.company_name, company_slug: formData.company_slug,
          status: formData.status, max_users: parseInt(formData.max_users),
          max_employees: parseInt(formData.max_employees),
        });
        showToast('Entreprise modifiée avec succès');
        setTimeout(() => onSuccess(), 1000);
      } else {
        const validUsers = users.filter(u => u.name.trim() && u.email.trim() && u.password);
        await superAdminApi.companies.create({
          company_name: formData.company_name, company_slug: formData.company_slug,
          status: formData.status, trial_days: parseInt(formData.trial_days),
          max_users: parseInt(formData.max_users), max_employees: parseInt(formData.max_employees),
          licence: { type_licence: formData.type_licence, duree_mois: parseInt(formData.duree_licence_mois) },
          users: validUsers,
        });
        const list = validUsers.map((u, i) => `${i + 1}. ${u.name} (${u.role})\n   📧 ${u.email}\n   🔑 ${u.password}`).join('\n\n');
        showToast('Entreprise créée avec succès');
        alert(`✅ Entreprise créée !\n\n${list}\n\n⚠️ Notez ces informations.`);
        onSuccess();
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ──
  const getPlanPrefix = (type) => ({ basique: 'B', standard: 'S', premium: 'P' }[type] || 'B');

  const PLAN_CARDS = [
    { type: 'basique',  accent: t.primary,     accentSoft: t.primarySoft },
    { type: 'standard', accent: t.warningText,  accentSoft: t.warningSoft },
    { type: 'premium',  accent: '#e07850',      accentSoft: 'rgba(224,120,80,0.08)' },
  ];

  // ── Modal bg ──
  const modalBg = t.mode === 'dark' ? '#111620' : '#ffffff';
  const formBg  = t.mode === 'dark' ? t.bg : '#f6f7f9';

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: t.bgOverlay, animation: 'cfFadeIn 0.2s ease-out' }}
    >
      <div
        className="rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col border"
        style={{ backgroundColor: modalBg, borderColor: t.border, boxShadow: t.shadowLg }}
      >

        {/* ── HEADER ── */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 border-b"
          style={{ borderColor: t.border }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.primaryDark})` }}>
              <Building2 size={17} className="text-white" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold" style={{ color: t.text }}>
                {isEditing ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}
              </h2>
              <p className="text-[11px] font-medium" style={{ color: t.textMuted }}>
                {isEditing ? 'Modification des informations' : 'Création avec utilisateurs et licence'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-colors" style={{ color: t.textMuted }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySofter}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <X size={18} />
          </button>
        </div>

        {/* ── FORM BODY ── */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: formBg }}>

          {/* INFOS ENTREPRISE */}
          <Section icon={Building2} title="Informations de l'entreprise" t={t}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label t={t}>Nom de l'entreprise</Label>
                <Input type="text" name="company_name" value={formData.company_name}
                  onChange={handleChange} placeholder="Ex: Daniced Group" error={errors.company_name} t={t} />
                <FieldError message={errors.company_name} t={t} />
              </div>
              <div>
                <Label t={t}>Identifiant (slug)</Label>
                <Input type="text" name="company_slug" value={formData.company_slug}
                  onChange={handleChange} placeholder="daniced-group" disabled={isEditing} error={errors.company_slug} t={t} />
                <FieldError message={errors.company_slug} t={t} />
              </div>
              <div>
                <Label t={t}>Statut</Label>
                <Select name="status" value={formData.status} onChange={handleChange} t={t}>
                  <option value="trial">Essai gratuit</option>
                  <option value="active">Active (payée)</option>
                  <option value="suspended">Suspendue</option>
                </Select>
              </div>
              {!isEditing && formData.status === 'trial' && (
                <div>
                  <Label t={t}>Durée essai (jours)</Label>
                  <Input type="number" name="trial_days" value={formData.trial_days}
                    onChange={handleChange} min="1" max="365" t={t} />
                </div>
              )}
            </div>
          </Section>

          {/* LICENCE */}
          {!isEditing && (
            <Section icon={Shield} title="Configuration de la licence" t={t}>
              {loadingPlans ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-5 h-5 border-2 rounded-full animate-spin"
                    style={{ borderColor: t.border, borderTopColor: t.primary }} />
                  <span className="ml-3 text-sm font-medium" style={{ color: t.textMuted }}>Chargement des plans…</span>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Plan cards */}
                  <div>
                    <Label t={t}>Type de licence</Label>
                    <div className="grid grid-cols-3 gap-3 mt-1">
                      {PLAN_CARDS.map(({ type, accent, accentSoft }) => {
                        const selected = formData.type_licence === type;
                        const plan = plans[type];
                        return (
                          <label key={type}
                            className="relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                            style={{
                              backgroundColor: selected ? accentSoft : t.bgInput,
                              borderColor: selected ? accent : t.border,
                              boxShadow: selected ? t.shadow : 'none',
                            }}
                            onMouseEnter={(e) => { if (!selected) e.currentTarget.style.borderColor = accent + '60'; }}
                            onMouseLeave={(e) => { if (!selected) e.currentTarget.style.borderColor = t.border; }}
                          >
                            <input type="radio" name="type_licence" value={type}
                              checked={selected} onChange={handleChange} className="sr-only" />
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-[13px] font-bold capitalize" style={{ color: t.text }}>{type}</span>
                              <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors"
                                style={{ borderColor: selected ? accent : t.textMuted, backgroundColor: selected ? accent : 'transparent' }}>
                                {selected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              {[
                                { label: 'Users', value: plan?.max_utilisateurs || 2 },
                                { label: 'Employés', value: plan?.max_employes || 25 },
                                { label: 'Stockage', value: `${((plan?.max_storage_mb || 1000) / 1000).toFixed(0)} GB` },
                              ].map(item => (
                                <div key={item.label} className="flex items-center justify-between text-[11px]">
                                  <span style={{ color: t.textMuted }}>{item.label}</span>
                                  <span className="font-bold tabular-nums" style={{ color: t.textSecondary }}>{item.value}</span>
                                </div>
                              ))}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label t={t}>Durée</Label>
                      <Select name="duree_licence_mois" value={formData.duree_licence_mois} onChange={handleChange} t={t}>
                        {[1, 3, 6, 12, 24, 36].map(m => (
                          <option key={m} value={m}>{m} mois</option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Label t={t}>Aperçu numéro</Label>
                      <div className="px-3.5 py-2.5 rounded-lg border" style={{ backgroundColor: t.bgInput, borderColor: t.border }}>
                        <span className="font-mono text-sm font-bold" style={{ color: t.primary }}>
                          DG-{getPlanPrefix(formData.type_licence)}XXX-HP
                          {String(new Date(Date.now() + formData.duree_licence_mois * 30 * 86400000).getMonth() + 1).padStart(2, '0')}
                          {String(new Date(Date.now() + formData.duree_licence_mois * 30 * 86400000).getFullYear()).slice(-2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* UTILISATEURS */}
          {!isEditing && !loadingPlans && (
            <Section icon={Users} title={`Utilisateurs (${users.length}/${plans[formData.type_licence]?.max_utilisateurs || 2})`} t={t}>
              {/* Info banner */}
              <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg border mb-4"
                style={{ backgroundColor: t.primarySofter, borderColor: t.primaryBorder }}>
                <Info size={14} style={{ color: t.primaryText }} className="flex-shrink-0 mt-0.5" />
                <p className="text-[12px] font-medium" style={{ color: t.primaryText }}>
                  <strong>Administrateur obligatoire.</strong> Les autres utilisateurs sont optionnels.
                </p>
              </div>

              <div className="space-y-3">
                {users.map((user, index) => {
                  const isAdmin = index === 0;
                  return (
                    <div key={index} className="p-4 rounded-xl border transition-colors"
                      style={{
                        backgroundColor: isAdmin
                          ? (t.mode === 'dark' ? 'rgba(251,191,36,0.04)' : 'rgba(251,191,36,0.04)')
                          : t.bgInput,
                        borderColor: isAdmin
                          ? (t.mode === 'dark' ? 'rgba(251,191,36,0.15)' : 'rgba(251,191,36,0.20)')
                          : t.border,
                      }}>

                      {/* User header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {isAdmin
                            ? <Crown size={13} style={{ color: t.warningText }} />
                            : <User size={13} style={{ color: t.textMuted }} />
                          }
                          <span className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: t.text }}>
                            {isAdmin ? 'Administrateur' : `Utilisateur ${index + 1}`}
                          </span>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: isAdmin ? t.warningSoft : t.bgTableHeader,
                            color: isAdmin ? t.warningText : t.textMuted,
                          }}>
                          {user.role}
                        </span>
                      </div>

                      {/* Fields */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Input type="text" value={user.name}
                            onChange={(e) => handleUserChange(index, 'name', e.target.value)}
                            placeholder="Nom complet" error={errors[`user_${index}_name`]} t={t} />
                          <FieldError message={errors[`user_${index}_name`]} t={t} />
                        </div>
                        <div>
                          <Input type="email" value={user.email}
                            onChange={(e) => handleUserChange(index, 'email', e.target.value)}
                            placeholder="Email" error={errors[`user_${index}_email`]} t={t} />
                          <FieldError message={errors[`user_${index}_email`]} t={t} />
                        </div>
                        <div>
                          <Input type="password" value={user.password}
                            onChange={(e) => handleUserChange(index, 'password', e.target.value)}
                            placeholder="Mot de passe" error={errors[`user_${index}_password`]} t={t} />
                          <FieldError message={errors[`user_${index}_password`]} t={t} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
        </form>

        {/* ── FOOTER ── */}
        <div className="px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0 border-t"
          style={{ borderColor: t.border, backgroundColor: modalBg }}>
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-sm font-medium border transition-all duration-200"
            style={{ borderColor: t.border, color: t.textSecondary, backgroundColor: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.primarySofter; e.currentTarget.style.borderColor = t.primaryBorder; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = t.border; }}>
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={loading || loadingPlans}
            className="px-6 py-2.5 rounded-lg text-sm font-bold text-white transition-all duration-200 flex items-center gap-2 disabled:opacity-40 active:scale-[0.97]"
            style={{ backgroundColor: t.primary }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = t.primaryDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.primary; }}>
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isEditing ? 'Modification…' : 'Création…'}
              </>
            ) : (
              <>
                <CheckCircle size={15} />
                {isEditing ? 'Modifier' : 'Créer l\'entreprise'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <Toast show={toast.show} message={toast.message} type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })} />
      )}

      <style>{`
        @keyframes cfFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        select option {
          background-color: ${modalBg};
          color: ${t.text};
        }
      `}</style>
    </div>,
    document.body
  );
};

export default CompanyForm;